import React from "react";
import "./Privacy.css";

const PrivacyPolicy = () => (
  <div className="privacy">
    <header>
      <h1>Privacy Policy</h1>
      <p>Last Updated: 04/15/2024</p>
    </header>

    <section>
      <h2>Introduction</h2>
      <p>
        EasePharm values your privacy and is committed to protecting your personal information.
        This Privacy Policy (“Policy”) explains how we collect, use, and protect your data when
        you use our services, which enable the convenient delivery of medications from pharmacies
        directly to your home. By accessing or using our website (
        <a href="https://easepharm.com">https://easepharm.com</a>) and services, you consent to the
        data practices described in this Policy. This Policy is designed to comply with applicable
        U.S. privacy laws and regulations.
      </p>
    </section>

    <section>
      <h2>Information We Collect</h2>
      <ul>
        <li>
          <strong>Personal Information:</strong> Includes details such as your name, address, email,
          phone number, billing information, and prescription data, provided by you or your
          pharmacy.
        </li>
        <li>
          <strong>Automatically Collected Information:</strong> Information gathered via cookies,
          analytics tools, and server logs, including IP address, browser type, and operating
          system.
        </li>
        <li>
          <strong>Non-Personal Information:</strong> Aggregated or anonymized demographic data (e.g.,
          age, preferences) to help us improve our services.
        </li>
      </ul>
    </section>

    <section>
      <h2>Why We Collect Information</h2>
      <ul>
        <li>To process and deliver your medication orders efficiently.</li>
        <li>To communicate with you about orders, service updates, and—if you opt in—special offers.</li>
        <li>To enhance our website functionality and user experience.</li>
        <li>To perform analytics and understand user needs for service improvement.</li>
      </ul>
    </section>

    <section>
      <h2>Use of Information</h2>
      <p>
        EasePharm does not sell, rent, or lease personal information to third parties. We use your
        data to:
      </p>
      <ul>
        <li>Process your orders and manage deliveries.</li>
        <li>Communicate about your account and service-related updates.</li>
        <li>Notify you about new features, offers, or products (with your consent).</li>
        <li>Conduct internal research to continually improve our services.</li>
      </ul>
    </section>

    <section>
      <h2>Data Retention</h2>
      <p>
        We retain personal information only for as long as necessary to fulfill the purposes
        outlined in this Policy and comply with legal, regulatory, and business obligations.
      </p>
    </section>

    <section>
      <h2>Disclosure of Information</h2>
      <p>Your data may be shared under the following circumstances:</p>
      <ul>
        <li>With pharmacies and delivery partners to complete your orders.</li>
        <li>In aggregated or anonymized form for analytical purposes.</li>
        <li>When required by law, regulation, or valid legal process.</li>
        <li>With trusted service providers under confidentiality agreements.</li>
        <li>
          To enforce our Terms of Service or protect the rights and safety of EasePharm and our
          users.
        </li>
      </ul>
    </section>

    <section>
      <h2>Security</h2>
      <p>
        EasePharm employs industry-standard security measures to protect your data. Sensitive
        information—such as prescription or payment details—is encrypted during transmission.
        Access to personal data is restricted to authorized personnel only.
      </p>
    </section>

    <section>
      <h2>Children’s Privacy</h2>
      <p>
        Our services are not intended for children under 14. We do not knowingly collect personal
        data from minors. If we learn we have inadvertently collected such information, we will
        delete it promptly or seek verified parental consent.
      </p>
    </section>

    <section>
      <h2>Your Rights</h2>
      <ul>
        <li>Access, review, and update your personal information.</li>
        <li>Opt out of receiving marketing communications.</li>
        <li>Request deletion of your personal data, subject to legal or operational requirements.</li>
      </ul>
    </section>

    <section>
      <h2>Changes to This Policy</h2>
      <p>
        We may update this Policy periodically. Any significant changes will be communicated
        through our website. Continued use of our services after updates indicates your acceptance
        of the revised Policy.
      </p>
    </section>

    <section>
      <h2>Contact Us</h2>
      <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
      <p>
        EasePharm<br />
        55 Clinton Pl, New Rochelle, NY 10801<br />
        Phone: (909) 552-4552<br />
        Email: <a href="mailto:info@easepharm.com">info@easepharm.com</a>
      </p>
    </section>

    <section>
      <h2>GDPR Compliance</h2>
      <p>
        EasePharm adheres to the General Data Protection Regulation (GDPR) for users within the
        European Union. Information you provide may be processed in the United States. By sharing
        your data, you consent to its transfer and processing in accordance with this Policy.
      </p>
    </section>

    <section>
      <h2>COPPA Compliance</h2>
      <p>
        EasePharm complies with the Children’s Online Privacy Protection Act (COPPA). We do not
        knowingly collect information from children under 13 without verified parental consent. If
        such data is discovered, we will delete it or seek appropriate authorization.
      </p>
    </section>

    <section>
      <h2>External Links</h2>
      <p>
        Our website may include links to external websites. EasePharm is not responsible for their
        privacy practices. We encourage you to review the privacy policies of any site you visit.
      </p>
    </section>

    <section>
      <h2>Acceptance of Terms</h2>
      <p>
        By using our website, you agree to the terms of this Privacy Policy. If you do not agree,
        please discontinue use of our services. Continued use after updates indicates acceptance of
        the revised Policy.
      </p>
    </section>
  </div>
);

export default PrivacyPolicy;
